import type { KitManifest } from '../../shared/discoverKits';

const manifest: KitManifest = {
    slug: 'production-editor',
    context: 'size2mix',
    name: 'Éditeur de production',
    description:
        "Édition d'un site de production : onglets (production-editor canvas, fuels, synthesis), modales de création (plant, taux EnR, fuel, boiler), et panneaux d'accordéons (paramètres de taux énergétique, fuels).",
    status: 'partial',
    owner: 'platform-team',
    tags: [
        'production',
        'graph',
        'react-flow',
        'editor',
        'site',
        'fuels',
        'energy-rate',
        'boiler',
        'enr',
    ],
    nemoSources: [
        {
            label: 'Page racine',
            path: 'apps/frontend-next/src/ui/views/pages/network/ProductionEditor.tsx',
            note: "Composition tabs + ProductionEditorProvider. Onglets configurés dans productionEditorTabs.config.tsx.",
        },
        {
            label: 'Configuration des onglets',
            path: 'apps/frontend-next/src/ui/views/pages/network/production-editor/productionEditorTabs.config.tsx',
            note: 'Liste des onglets visibles selon hot-water / cold-water et flag alpha.',
        },
        {
            label: 'Context + state global',
            path: 'apps/frontend-next/src/ui/views/context/ProductionEditorContext.tsx',
            note: 'API de référence pour le hook useProductionEditor.',
        },
        {
            label: 'Tables et sections',
            path: 'apps/frontend-next/src/ui/views/pages/network/production-editor/',
            note: 'EnergyMixesTable, FuelContractsTable, PollutantsTable, MixTargetsTable, PowerContractsTable, EnergyMixSection.',
        },
        {
            label: 'Modales de création/édition',
            path: 'apps/frontend-next/src/ui/views/modals/production-editor/',
            note: 'EnergyMixCreateModal, FuelContractCreateModal/EditModal, PowerContractCreateModal, etc. Pattern react-hook-form + yup.',
        },
        {
            label: 'Documentation feature',
            path: 'docs/production-editor/README.md',
            note: 'Architecture, hook useProductionEditor, exemples.',
        },
    ],
    sharedComponents: [
        '@shared/production-editor (Provider + Canvas + OverlaySidebar + NODE_TYPES + getNodeIcon)',
        '@shared/production-editor/nodes (Asset, Contract, Group, GroupMember, CustomNode)',
        '@shared/DataTable (tables des onglets : fuels, mixes, polluants…)',
        '@shared/AccordionItem (panneaux paramètres énergétiques, fuels accordion)',
        "@shared/modal/CenterModal (modales de création : plant, fuel, boiler, EnR rate)",
        "@shared/modal/RightSideModal (panneaux d'édition latéraux le cas échéant)",
    ],
    views: [
        {
            id: 'production-editor-tab',
            label: 'Onglet — Production Editor (canvas)',
            description:
                "Vue React Flow principale du site : actifs reliés, drag-and-drop depuis la sidebar de composants. Onglet \"editor\".",
            prototypeSlug: 'ref-network-production',
            screenshot: '01-production-editor-tab.png',
        },
        {
            id: 'fuels-tab',
            label: 'Onglet — Fuels',
            description:
                "Table des combustibles (fuels) configurés sur le site. Permet création / édition / suppression depuis la table.",
            screenshot: '02-fuels-tab.png',
        },
        {
            id: 'synthesis-tab',
            label: 'Onglet — Synthesis',
            description:
                "Vue de synthèse récapitulative du site (KPIs et états agrégés des actifs et contrats).",
            screenshot: '03-synthesis-tab.png',
        },
        {
            id: 'create-new-production-plant',
            label: "Modale — Création d'un plant",
            description:
                "Formulaire de création d'un nouveau plant (nœud parent dans l'éditeur). Première étape avant d'y rattacher des actifs.",
            screenshot: '04-create-new-production-plant.png',
        },
        {
            id: 'create-new-enr-rate',
            label: "Modale — Création d'un taux EnR",
            description:
                "Formulaire de création d'un taux d'énergie renouvelable (EnR rate) à appliquer sur un actif ou un contrat.",
            screenshot: '05-create-new-enr-rate.png',
        },
        {
            id: 'create-fuel-with-plants',
            label: "Modale — Création d'un fuel (avec plants sélectionnés)",
            description:
                "Création d'un nouveau combustible (fuel) avec sélection multiple des plants/actifs auxquels il s'applique. Pattern de modale large avec sélecteur de plants.",
            screenshot: '06-create-new-fuel-with-plants-selected.png',
        },
        {
            id: 'boiler-creation-form',
            label: "Formulaire — Création d'une chaudière (boiler)",
            description:
                "Formulaire long de création d'une chaudière : caractéristiques techniques, plage de puissance, rendement, paramètres avancés. Représentatif du formulaire de création d'asset.",
            screenshot: '07-boiler-creation-form.png',
        },
        {
            id: 'energy-rate-parameters-accordion',
            label: 'Panneau — Paramètres de taux énergétique (accordion)',
            description:
                "Panneau d'accordéons rétractables exposant les paramètres détaillés d'un taux énergétique appliqué (sections : conditions, prix, contraintes, calendrier).",
            screenshot: '08-energy-rate-parameters-accordion.png',
        },
        {
            id: 'fuels-accordion',
            label: 'Panneau — Fuels (accordion)',
            description:
                "Vue alternative aux fuels en panneau d'accordéons : un fuel par section dépliable, avec ses paramètres et plants associés inline.",
            screenshot: '09-fuels-accordion.png',
        },
    ],
    notes: `
- Manifest reconstruit le 30 avril 2026 à partir des 9 captures réelles fournies par
  amourad. Toutes les vues listées ci-dessus ont un screenshot disponible.
- L'éditeur de production réel a d'autres vues non encore capturées (mix énergétiques,
  cibles, polluants, contrats électricité, contrats combustibles détaillés, modale
  d'édition de nœud). À ajouter au fil des captures supplémentaires — dans ce cas
  étendre 'views' avec un nouvel id, déposer le PNG, et incrémenter la numérotation.
- Hot-water vs cold-water : certains onglets sont conditionnels (cf.
  productionEditorTabs.config.tsx). À documenter dans la description de chaque vue
  concernée.
- Le wrapper @shared/production-editor reste la voie unique pour reproduire le canvas.
  Pour les modales de création (plant, fuel, boiler, EnR), partir de @shared/modal/
  CenterModal + react-hook-form + yup (pattern Nemo réel).
- Le boiler-creation-form (vue 07) est probablement le plus complexe : c'est une
  bonne référence pour tout proto de "formulaire d'asset". À considérer comme
  archetype "long form" en plus de l'Archetype 3 existant
  (trajectory-optimization-create-form, multi-tab).

ARCHETYPE DE RÉFÉRENCE (RÈGLE DURE) :
  Tout prototype qui touche à l'éditeur de production DOIT partir de
  'src/prototypes/size2mix/ref-network-production' et réutiliser les composants du
  kit @shared/production-editor (NODE_TYPES, ProductionEditorOverlaySidebar,
  ProductionEditorCanvas, getNodeIcon). Si un élément manque dans le kit
  (nouveau type de nœud, nouveau type d'edge, nouvelle section sidebar),
  ÉTENDRE LE KIT D'ABORD, puis l'utiliser dans le prototype. Ne jamais
  ré-implémenter en local : un proto qui dérive du style Nemo est rejeté.
`.trim(),
};

export default manifest;
